/**
 * Pantry Recipe Service
 *
 * Calculates recipe suggestions for items in the pantry,
 * scoring them by ingredient overlap.
 */

import { InventoryItem } from '../redux/inventorySlice';
import { PaprikaFreshness, Paprika } from '../styles/theme';

/** Minimal recipe shape supporting both PersonalRecipeIndexEntry and RecipeIndexEntry */
export interface RecipeForScore {
  id: string;
  title: string;
  image?: string;
  ingredients: string[];
  readyInMinutes?: number;
  dishTypes?: string[];
  cuisines?: string[];
  diets?: string[];
  servings?: string | number;
  sourceRating?: number;
  [key: string]: any; // Allow extra fields
}

export interface PantryRecipeSuggestion {
  entry: RecipeForScore;
  score: number;            // 0-100, round(inStock / total * 100)
  matched: string[];        // ingredient display names in stock
  missing: string[];        // ingredient display names not in stock
}

/**
 * Fuzzy match an ingredient name against active inventory items.
 * Returns true if the ingredient matches at least one active item.
 *
 * Fuzzy match using a proven scoring approach (originally from mealAlternativesService,
 * now removed from the codebase):
 * - Normalize to lowercase
 * - Check if ingredient includes any active item name (or vice versa)
 */
function fuzzyMatchesInventory(ingredientName: string, activeInventory: string[]): boolean {
  const norm = ingredientName.toLowerCase();
  return activeInventory.some(
    n => norm.includes(n) || n.includes(norm)
  );
}

// Words that carry no meaning when deciding whether a recipe uses an item.
const ITEM_TOKEN_STOPWORDS = new Set([
  'de', 'du', 'des', 'le', 'la', 'les', 'un', 'une', 'et', 'en', 'au', 'aux',
  'the', 'of', 'a', 'an', 'and', 'with',
  'frais', 'fraiche', 'fraiches', 'fresh',
]);

const stripAccents = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Meaningful, singularized tokens of an item name ("Filets de saumon" → ["filet","saumon"]). */
function itemCoreTokens(name: string): string[] {
  return stripAccents(name)
    .split(/[^a-z]+/)
    .filter(t => t.length >= 3 && !ITEM_TOKEN_STOPWORDS.has(t))
    .map(t => t.replace(/s$/, ''));
}

/**
 * Does this recipe actually use the selected item?
 * Token match with word boundaries and plural tolerance, so "Filets de saumon"
 * matches "600 g de filet de saumon" but "ail" does not match "travail".
 */
function recipeUsesItem(recipe: RecipeForScore, itemName: string): boolean {
  const tokens = itemCoreTokens(itemName);
  if (tokens.length === 0 || !recipe.ingredients) return false;
  return recipe.ingredients.some(ing => {
    const ingNorm = stripAccents(ing);
    return tokens.some(tok => new RegExp(`(^|[^a-z])${tok}s?([^a-z]|$)`).test(ingNorm));
  });
}

/**
 * Get the color for a recipe match score badge.
 * Uses Paprika freshness tones:
 * - ≥80: PaprikaFreshness.fresh.text (high match)
 * - 60-79: Paprika.scoreMid (mid match)
 * - <60: Paprika.accent (low match)
 */
export function getScoreBadgeColor(score: number): string {
  if (score >= 80) return PaprikaFreshness.fresh.text;
  if (score >= 60) return Paprika.scoreMid;
  return Paprika.accent;
}

/**
 * Get recipe suggestions for a pantry item.
 *
 * Selection rule: Keep recipes where at least one recipe ingredient
 * fuzzy-matches the item name. Rank by score desc, then by fewer total
 * ingredients (to prefer simpler recipes on tie). Return top `limit`.
 *
 * @param item The pantry item to find recipes for
 * @param index All available recipes (PersonalRecipeIndexEntry or RecipeIndexEntry)
 * @param inventory All active inventory items (for matching)
 * @param limit Max number of suggestions to return (default 5)
 */
export function recipesForPantryItem(
  item: InventoryItem,
  index: RecipeForScore[],
  inventory: InventoryItem[],
  limit = 5,
): PantryRecipeSuggestion[] {
  // Get active inventory as lowercase names for fuzzy matching
  const activeNames = inventory
    .filter(i => i.status === 'active')
    .map(i => i.name.toLowerCase());

  // Filter: keep only recipes that actually use the SELECTED item —
  // suggesting recipes that merely share some other pantry ingredient
  // defeats the "cook it before it spoils" purpose.
  const candidates = index.filter(recipe => {
    if (!recipe.ingredients || recipe.ingredients.length === 0) return false;
    return recipeUsesItem(recipe, item.name);
  });

  // Score: for each recipe, count how many ingredients are in active inventory
  const scored = candidates.map((recipe): PantryRecipeSuggestion => {
    const matched: string[] = [];
    const missing: string[] = [];

    for (const ing of recipe.ingredients) {
      if (fuzzyMatchesInventory(ing, activeNames)) {
        matched.push(ing);
      } else {
        missing.push(ing);
      }
    }

    const score = Math.round((matched.length / recipe.ingredients.length) * 100);

    return {
      entry: recipe,
      score,
      matched,
      missing,
    };
  });

  // Sort: by score descending, then by fewer total ingredients
  scored.sort((a, b) => {
    if (a.score !== b.score) {
      return b.score - a.score; // higher score first
    }
    return a.entry.ingredients.length - b.entry.ingredients.length; // fewer ingredients first
  });

  return scored.slice(0, limit);
}
